/**
 * Big Divide: 2443 ÷ 17 without a worksheet. The pile of 2443 is a bar. You
 * take away batches of 17 — a hundred of them, ten of them, one of them — and
 * each batch you take lands as a tile. When fewer than 17 are left, the tiles
 * add up to the answer and what is left in the bar is the remainder.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Home, RotateCcw, Undo2, Wand2 } from 'lucide-preact';
import type { DivideState } from '../../lib/division.ts';
import {
  chunkSizes,
  answer,
  biggestChunk,
  canTake,
  checkLine,
  createDivide,
  divideHint,
  isDone,
  quotientSoFar,
  remaining,
  solve,
  take,
  undo,
} from '../../lib/division.ts';
import type { Spark } from '../../engine/render.ts';
import { drawSparks, spawnSparks, updateSparks } from '../../engine/render.ts';
import { partyColors, token } from '../../engine/palette.ts';
import { play } from '../../engine/audio.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import { Chip, Coach, ToyButton } from '../../ui/controls.tsx';
import { NumberPad } from '../../ui/NumberPad.tsx';

const MAX_DIVIDEND = 999999;
const MAX_DIVISOR = 9999;
const BAR_TOP = 158;
const BAR_HEIGHT = 52;

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

type Editing = 'dividend' | 'divisor' | null;

export function DivideToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sparksRef = useRef<Spark[]>([]);
  const shownRef = useRef(0);

  const [state, setState] = useState<DivideState>(() => createDivide(2443, 17));
  const [editing, setEditing] = useState<Editing>(null);

  const done = isDone(state);
  const { quotient, remainder } = answer(state);

  const live = useRef(state);
  live.current = state;

  // Celebrate the moment the pile is smaller than the divisor.
  const wasDone = useRef(done);
  useEffect(() => {
    if (done && !wasDone.current && state.chunks.length > 0) {
      play('yay', 0.8);
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) spawnSparks(sparksRef.current, { x: rect.width / 2, y: BAR_TOP + BAR_HEIGHT }, partyColors(), 24, 380);
    }
    wasDone.current = done;
  }, [done, state.chunks.length]);

  useSurface(canvasRef, {
    onFrame: (ctx, dt, { width, height }) => {
      updateSparks(sparksRef.current, dt);
      const s = live.current;
      const target = remaining(s);
      // Ease the bar towards the new value so a batch visibly leaves the pile.
      shownRef.current += (target - shownRef.current) * (1 - Math.exp(-dt * 9));
      if (Math.abs(target - shownRef.current) < 0.6) shownRef.current = target;

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      const pad = 20;
      const barW = width - pad * 2;
      const fraction = s.dividend > 0 ? Math.max(0, Math.min(1, shownRef.current / s.dividend)) : 0;

      // The pile: what is left, with the taken part shaded out behind it.
      roundRect(ctx, pad, BAR_TOP, barW, BAR_HEIGHT, 14);
      ctx.fillStyle = token('--paper-deep');
      ctx.fill();

      const takenW = barW * (1 - fraction);
      if (takenW > 1) {
        ctx.save();
        roundRect(ctx, pad, BAR_TOP, barW, BAR_HEIGHT, 14);
        ctx.clip();
        ctx.globalAlpha = 0.3;
        ctx.fillStyle = token('--crayon-blue');
        ctx.fillRect(pad, BAR_TOP, takenW, BAR_HEIGHT);
        ctx.globalAlpha = 1;
        ctx.restore();
      }

      if (fraction > 0.001) {
        ctx.save();
        roundRect(ctx, pad, BAR_TOP, barW, BAR_HEIGHT, 14);
        ctx.clip();
        ctx.fillStyle = token('--crayon-blue');
        ctx.fillRect(pad + takenW, BAR_TOP, barW - takenW, BAR_HEIGHT);
        ctx.restore();
      }

      ctx.lineWidth = 3;
      ctx.strokeStyle = token('--ink');
      roundRect(ctx, pad, BAR_TOP, barW, BAR_HEIGHT, 14);
      ctx.stroke();

      // How many are left, written on the pile.
      const leftLabel = String(Math.round(shownRef.current));
      ctx.font = `700 ${BAR_HEIGHT * 0.52}px ${FONT}`;
      ctx.textBaseline = 'middle';
      const labelW = ctx.measureText(leftLabel).width;
      const remainW = barW - takenW;
      const inside = remainW > labelW + 24;
      ctx.textAlign = inside ? 'center' : 'left';
      ctx.fillStyle = inside ? token('--panel') : token('--ink');
      ctx.fillText(
        leftLabel,
        inside ? pad + takenW + remainW / 2 : pad + Math.max(10, takenW - labelW - 10),
        BAR_TOP + BAR_HEIGHT / 2
      );

      ctx.font = `600 15px ${FONT}`;
      ctx.textAlign = 'left';
      ctx.fillStyle = token('--ink-soft');
      ctx.fillText('left in the pile', pad + 2, BAR_TOP - 16);

      // Each batch taken, as a tile that adds up to the answer.
      const tileTop = BAR_TOP + BAR_HEIGHT + 34;
      ctx.fillText('batches of ' + s.divisor + ' taken away', pad + 2, tileTop - 14);

      const tileW = 50;
      const tileH = 36;
      const gap = 6;
      const perRow = Math.max(1, Math.floor((barW + gap) / (tileW + gap)));
      const rowsThatFit = Math.max(1, Math.floor((height - 96 - tileTop) / (tileH + gap)));
      const capacity = perRow * rowsThatFit;
      // Never drop batches in silence: if they will not all fit, the last slot
      // says how many are not being shown. The count is taken from what was
      // actually drawn — the marker itself occupies a slot, so counting from
      // the capacity instead would under-report by one.
      const shown = s.chunks.length > capacity ? capacity - 1 : s.chunks.length;
      const hidden = s.chunks.length - shown;

      s.chunks.slice(0, shown).forEach((chunk, i) => {
        const row = Math.floor(i / perRow);
        const col = i % perRow;
        const x = pad + col * (tileW + gap);
        const y = tileTop + row * (tileH + gap);
        roundRect(ctx, x, y, tileW, tileH, 10);
        ctx.fillStyle = token('--crayon-blue');
        ctx.fill();
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = token('--ink');
        ctx.stroke();
        ctx.fillStyle = token('--panel');
        ctx.font = `700 18px ${FONT}`;
        ctx.textAlign = 'center';
        ctx.fillText(String(chunk.multiple), x + tileW / 2, y + tileH / 2);
      });

      if (hidden > 0) {
        const row = Math.floor(shown / perRow);
        const col = shown % perRow;
        const x = pad + col * (tileW + gap);
        const y = tileTop + row * (tileH + gap);
        roundRect(ctx, x, y, tileW, tileH, 10);
        ctx.fillStyle = token('--paper-deep');
        ctx.fill();
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = token('--ink-faint');
        ctx.stroke();
        ctx.fillStyle = token('--ink-soft');
        ctx.font = `700 15px ${FONT}`;
        ctx.textAlign = 'center';
        ctx.fillText(`+${hidden}`, x + tileW / 2, y + tileH / 2);
      }

      if (s.chunks.length > 0) {
        const rows = Math.min(rowsThatFit, Math.ceil(s.chunks.length / perRow));
        const y = Math.min(height - 78, tileTop + rows * (tileH + gap) + 12);
        ctx.textAlign = 'left';
        ctx.fillStyle = token('--ink');
        ctx.font = `700 22px ${FONT}`;
        ctx.fillText(`= ${quotientSoFar(s)} lots of ${s.divisor}`, pad + 2, y);
      }

      drawSparks(ctx, sparksRef.current);
    },
  });

  const takeChunk = (multiple: number) => {
    const s = live.current;
    if (!canTake(s, multiple)) {
      play('thud', 0.5);
      return;
    }
    play('count', Math.min(1, multiple / 100 + 0.3));
    setState(take(s, multiple));
  };

  const restart = (dividend = state.dividend, divisor = state.divisor) => {
    shownRef.current = dividend;
    setState(createDivide(dividend, divisor));
  };

  const hint = divideHint(state);
  const nudge = biggestChunk(state);

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Big divide" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <div class="topbar__group">
            <ToyButton
              round
              label="Take the batches for me"
              hideLabel
              disabled={done}
              onClick={() => {
                play('sparkle', 0.7);
                setState(solve(live.current));
              }}
            >
              <Wand2 size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton
              round
              label="Undo"
              hideLabel
              disabled={state.chunks.length === 0}
              onClick={() => {
                play('pop', 0.4);
                setState(undo(live.current));
              }}
            >
              <Undo2 size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton
              round
              label="Start again"
              hideLabel
              onClick={() => {
                play('whoosh', 0.6);
                restart();
              }}
            >
              <RotateCcw size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>

        <p class="readout">
          {done && state.chunks.length > 0 ? (
            <>
              <strong>
                {state.dividend} ÷ {state.divisor} = {quotient}
              </strong>
              <span>{remainder > 0 ? `remainder ${remainder}` : 'exactly'}</span>
            </>
          ) : (
            <>
              <strong>
                How many {state.divisor}s in {state.dividend}?
              </strong>
            </>
          )}
        </p>

        <Coach hideAfter={done ? 60000 : 12000}>{done && state.chunks.length > 0 ? checkLine(state) : hint}</Coach>
      </div>

      <div class="dock">
        {tabs}
        <div class="dock__row">
          <Chip label={String(state.dividend)} onClick={() => setEditing('dividend')} />
          <span class="dock__label">shared into groups of</span>
          <Chip label={String(state.divisor)} onClick={() => setEditing('divisor')} />
        </div>
        <div class="dock__row dock__row--scroll" role="group" aria-label="Take away a batch">
          {chunkSizes(state).map((size) => (
            <button
              key={size}
              type="button"
              class={`batch${nudge === size ? ' batch--next' : ''}`}
              disabled={!canTake(state, size)}
              onClick={() => takeChunk(size)}
            >
              <strong>{size}</strong>
              <span>
                × {state.divisor} = {size * state.divisor}
              </span>
            </button>
          ))}
        </div>
      </div>

      {editing && (
        <NumberPad
          title={editing === 'dividend' ? 'How many altogether?' : 'Groups of how many?'}
          value={editing === 'dividend' ? state.dividend : state.divisor}
          min={editing === 'dividend' ? 0 : 1}
          max={editing === 'dividend' ? MAX_DIVIDEND : MAX_DIVISOR}
          onDone={(value) => {
            setEditing(null);
            if (editing === 'dividend') restart(value, state.divisor);
            else restart(state.dividend, value);
          }}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}

const FONT = 'Fredoka, ui-rounded, system-ui, sans-serif';

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}
